/**
 * ComposingHeader Component
 *
 * Header for the composing mode in iMessage-style conversation creation.
 * Shows "To:" label with the RecipientSelector component and a cancel button.
 */

import React from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RecipientSelector } from './RecipientSelector';
import type { ConversationParticipant, Conversation } from '@/types/messaging';

interface ComposingHeaderProps {
  recipients: ConversationParticipant[];
  onAddRecipient: (recipient: ConversationParticipant) => void;
  onRemoveRecipient: (userId: string) => void;
  onOpenExisting: (conversation: Conversation) => void;
  onCancel: () => void;
  disabled?: boolean;
}

export const ComposingHeader: React.FC<ComposingHeaderProps> = ({
  recipients,
  onAddRecipient,
  onRemoveRecipient,
  onOpenExisting,
  onCancel,
  disabled = false,
}) => {
  return (
    <div className="flex items-start gap-3 px-4 py-3 border-b border-border bg-card">
      {/* To: label */}
      <span className="text-sm font-medium text-muted-foreground pt-2 shrink-0">To:</span>

      {/* Recipient selector */}
      <RecipientSelector
        recipients={recipients}
        onAddRecipient={onAddRecipient}
        onRemoveRecipient={onRemoveRecipient}
        onOpenExisting={onOpenExisting}
        disabled={disabled}
        autoFocus={true}
        placeholder="Search connections..."
      />

      {/* Cancel button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={onCancel}
        className="shrink-0 h-8 w-8"
        aria-label="Cancel composing"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default ComposingHeader;
